/**
 * dtcgToCssVars — W3C DTCG design-token bag → shadcn CSS-var map.
 *
 * Saf, deterministik util. DB/DOM/HTTP YOK.
 *
 * Akış:
 *   - `TOKEN_MAP` üzerinden bilinen her kanonik DTCG path'i gezer.
 *   - Path bag'de çözülürse: renk → `transformColor`, dimension → passthrough.
 *   - Bilinmeyen / eksik path → sessizce DROP (çıktıya dahil edilmez; hata atılmaz).
 *   - Çıktı obje key sırası `TOKEN_MAP` sırasıyla deterministik.
 *
 * DTCG ağaç convention'ı (tokenMap.ts'te dökümante):
 *   - `color.primary` hem grup default değerini hem `foreground` alt-token'ını taşıyabilir.
 *   - Değer çözümü esnek: W3C `$value`, kısa `value`, ham renk objesi ya da ham string desteklenir.
 */
import { transformColor, } from './colorTransform.js';
import { TOKEN_MAP } from './tokenMap.js';
/** Düz obje mi? (null ve array hariç) */
function isPlainObject(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
/** Dot-path'i bag içinde gezer; bulamazsa undefined. */
function resolvePath(bag, dotPath) {
    let node = bag;
    for (const seg of dotPath.split('.')) {
        if (!isPlainObject(node))
            return undefined;
        node = node[seg];
    }
    return node;
}
/**
 * Bir DTCG node'undan ham token değerini çıkarır.
 * Öncelik: W3C `$value` → kısa `value` → node'un kendisi (ham string/obje).
 * Grup node'larında (alt-token taşıyan) yalnız `$value`/`value` kabul edilir; düz grup objesi değer DEĞİLDİR.
 */
function extractRawValue(node) {
    if (node === undefined || node === null)
        return undefined;
    if (isPlainObject(node)) {
        if ('$value' in node)
            return node.$value;
        if ('value' in node)
            return node.value;
        // DTCG color objesi mi? (colorSpace/components/hex) — değerin kendisi.
        if ('colorSpace' in node || 'components' in node || 'hex' in node) {
            return node;
        }
        // Salt grup (yalnız alt-token'lar) → değer yok.
        return undefined;
    }
    // Ham scalar (string/number).
    return node;
}
/** Renk girdisini transformColor'ın kabul ettiği tipe daraltır. */
function asColorInput(raw) {
    if (typeof raw === 'string')
        return raw;
    if (isPlainObject(raw))
        return raw;
    return undefined;
}
/** Dimension (radius) passthrough — string ya da number → string. */
function asDimension(raw) {
    if (typeof raw === 'string')
        return raw.trim();
    if (typeof raw === 'number')
        return String(raw);
    return undefined;
}
/** Tek bir map girişini çözer; çözülemezse undefined (drop). */
function resolveEntry(bag, entry, target) {
    const raw = extractRawValue(resolvePath(bag, entry.dtcgPath));
    if (raw === undefined)
        return undefined;
    if (entry.kind === 'dimension') {
        return asDimension(raw);
    }
    const colorInput = asColorInput(raw);
    if (colorInput === undefined)
        return undefined;
    return transformColor(colorInput, target);
}
/**
 * DTCG token bag'i shadcn CSS-var map'ine çevirir.
 *
 * @param bag   W3C DTCG token bag (iç içe grup/token objesi).
 * @param opts  `{ target?: 'oklch' | 'hsl' }` — varsayılan 'oklch'.
 * @returns     `Record<cssVar, transformedValue>`. Yalnız çözülebilen bilinen key'ler dahil.
 *              Bilinmeyen DTCG path'ler ve parse edilemeyen değerler sessizce DROP edilir.
 */
export function dtcgToCssVars(bag, opts = {}) {
    const target = opts.target ?? 'oklch';
    const out = {};
    if (!isPlainObject(bag))
        return out;
    for (const entry of TOKEN_MAP) {
        const value = resolveEntry(bag, entry, target);
        if (value !== undefined) {
            out[entry.cssVar] = value;
        }
    }
    return out;
}
//# sourceMappingURL=dtcgToCssVars.js.map